import React from 'react';
import { motion } from 'framer-motion';
import { Spinner, Alert } from 'reactstrap';
import { Clock, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { translateActivity } from '../../helpers/activityTranslator';

/**
 * Formats a date-time string for display.
 */
const formatDateTime = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

/**
 * Formats a date string for due date display (shorter).
 */
const formatDueDate = (dateStr, fallbackStr) => {
  if (!dateStr) return fallbackStr;
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/**
 * Extracts initials from a user name.
 */
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return parts[0][0]?.toUpperCase() || '?';
};

/**
 * RecentActivityWidget - Displays a chronological feed of work order activities
 * grouped by work order.
 */
const RecentActivityWidget = ({
  data,
  loading,
  loadingMore,
  error,
  onLoadMore,
  onWorkOrderClick,
}) => {
  const { t } = useTranslation();
  const { groups = [], hasMore = false } = data || {};

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="card border-0 shadow-sm"
      style={{ borderRadius: '16px' }}
    >
      <div className="card-body p-4">
        {/* Header */}
        <h5 className="fw-bold mb-3">{t('shared.tab_recent_activity', 'Recent Activity')}</h5>

        {/* Content States */}
        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
            <p className="text-muted mt-3">{t('reporting.recent_activity.loading', 'Loading recent activity...')}</p>
          </div>
        ) : error ? (
          <Alert color="danger" className="mb-0">
            <strong>{t('shared.error', 'Error:')}</strong> {error}
          </Alert>
        ) : groups.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <Clock size={48} className="mb-3" />
            <p className="mb-0">{t('reporting.recent_activity.empty', 'No recent activity found for the selected period and filters.')}</p>
          </div>
        ) : (
          <>
            {/* Activity Feed */}
            <div className="border rounded" style={{ borderRadius: '12px' }}>
              {groups.map((group, groupIndex) => (
                <motion.div
                  key={group.workOrderId}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: groupIndex * 0.03 }}
                  className={groupIndex < groups.length - 1 ? 'border-bottom' : ''}
                  style={{ padding: '16px 20px' }}
                >
                  {/* Group Header */}
                  <div className="mb-2" style={{ fontSize: '0.85rem' }}>
                    <span className="text-muted">{t('reporting.recent_activity.in_work_order', 'In Work Order')} </span>
                    <a
                      href="#"
                      className="fw-medium"
                      style={{ color: 'var(--bs-primary)', textDecoration: 'none' }}
                      onClick={(e) => {
                        e.preventDefault();
                        if (onWorkOrderClick) {
                          onWorkOrderClick({
                            id: group.workOrderId,
                            ticketNumber: group.ticketNumber,
                            title: group.title,
                          });
                        }
                      }}
                    >
                      #{group.ticketNumber} - {group.title} - {t('shared.due', 'Due')} {formatDueDate(group.dueDate, t('shared.no_due_date', 'No due date'))}
                    </a>
                  </div>

                  {/* Activity Items */}
                  {group.activities.map((item) => (
                    <div
                      key={item.id}
                      className="d-flex gap-3 py-2"
                      style={{ paddingLeft: '16px' }}
                    >
                      {/* Avatar */}
                      {item.userProfileImage && !item.userProfileImage.includes('profile.png') ? (
                        <img
                          src={item.userProfileImage}
                          alt={item.userName}
                          className="rounded-circle"
                          style={{
                            width: 36,
                            height: 36,
                            objectFit: 'cover',
                            flexShrink: 0,
                          }}
                        />
                      ) : (
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold"
                          style={{
                            width: 36,
                            height: 36,
                            backgroundColor: '#3980c0',
                            fontSize: '0.75rem',
                            flexShrink: 0,
                          }}
                        >
                          {getInitials(item.userName)}
                        </div>
                      )}

                      {/* Content */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="d-flex align-items-baseline gap-2 flex-wrap">
                          <span className="fw-medium" style={{ fontSize: '0.85rem' }}>
                            {item.userName}
                          </span>
                          <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                            {formatDateTime(item.createdAt)}
                          </span>
                        </div>

                        <p className="mb-0 mt-1" style={{ fontSize: '0.85rem', color: 'var(--bs-body-color)' }}>
                          {translateActivity(item, t)}
                        </p>
                      </div>
                    </div>
                  ))}
                </motion.div>
              ))}
            </div>

            {/* Load More */}
            {hasMore && (
              <div className="text-center mt-3">
                <motion.button
                  className="btn btn-outline-primary d-inline-flex align-items-center gap-2"
                  onClick={onLoadMore}
                  disabled={loadingMore}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {loadingMore ? (
                    <>
                      <RefreshCw
                        size={16}
                        style={{ animation: 'spin 1s linear infinite' }}
                      />
                      {t('shared.loading', 'Loading...')}
                    </>
                  ) : (
                    t('shared.load', 'Load More')
                  )}
                </motion.button>
              </div>
            )}
          </>
        )}
      </div>
    </motion.div>
  );
};

export default RecentActivityWidget;
